import { useEffect, useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import Image from "next/image";
import SubLayout from "@/containers/layout/LandingLayout/SubLayout";

import { formatPrice } from "@/utils/formatPrice";
import { BudgetModel } from "./types";
import { CalculatorDesktop, Buttons, Description } from "./components";
import { useWindowSize } from "@/shared/hooks/useWindowSize";
import Screen from "@/constants/screens";
import CalculatorMobile from "./components/CalculatorMobile";
import { useNowCalculatorBudget } from "@/shared/hooks/useNowCalculatorBudget";
import HorizontalBar from "@/atoms/HorizontalBar";

const MIN_ANNUAL_INCOME = 30000;
const MAX_ANNUAL_INCOME = 180000;
const DEFAULT_ANNUAL_INCOME = 70000;

const BudgetCalculator = ({ onHowIsPossible }: { onHowIsPossible?: () => void } = {}) => {
  const [annualIncome, setAnnualIncome] = useState<number>(DEFAULT_ANNUAL_INCOME);
  const [keyzyBudget] = useNowCalculatorBudget(annualIncome);
  const [imageUrl, setImageUrl] = useState("/landing/buyingBudget/calc-bg-desktop.svg");
  const { width } = useWindowSize();

  const sliderOnChange = (values: number) => {
    setAnnualIncome(values);
  };

  useEffect(() => {
    if (width < Screen.Tablet_768) setImageUrl("/landing/buyingBudget/calc-bg-mobile.svg");

    if (width >= Screen.Tablet_768) setImageUrl("/landing/buyingBudget/calc-bg-tablet.svg");

    if (width >= Screen.DESKTOP_1280) setImageUrl("/landing/buyingBudget/calc-bg-desktop.svg");
  }, [width]);
  const imageMobileUrl = "/landing/buyingBudget/mobile-calculator-bg.svg";
  return (
    <div className="md:bg-landing-ivory xxs:mt-28 sm:mt-0 pb-12 relative h-[62rem] md:h-[43.625rem] lg:h-[40.625rem] text-landing-blue ">
      <div className="absolute  -top-52 md:hidden">
        <Image
          src={imageMobileUrl}
          height={440}
          width={500}
          alt="House background"
          loading="eager"
          priority={true}
        />
      </div>
      <div id="calculator" className="absolute top-16 xl:-top-48 z-50" />
      <SubLayout allowOverflow>
        <div className="relative flex flex-col w-full mx-auto max-w-7xl md:flex-row gap-24 lg:gap-0 xl:gap-0">
          <div className="relative -top-40 md:-top-[7.3rem] xl:-top-[17.6rem] xl:min-w-fit md:w-[45%] lg:1/2 xl:w-3/5">
            <div className="flex flex-col md:px-8 md:w-[28rem] xl:px-16 xl:w-[45.6875rem] pb-16 xl:pb-36 relative ">
              {width >= Screen.Tablet_768 && (
                <Image
                  src={imageUrl}
                  alt="House background"
                  layout="fill"
                  priority={true}
                  loading="eager"
                />
              )}

              {width < Screen.Tablet_768 && (
                <div className="absolute -z-10 h-[24rem] -left-5 -right-5 -top-6 ">
                  <Image
                    src={imageUrl}
                    alt="House background"
                    layout="fill"
                    objectFit="cover"
                    priority={true}
                    loading="eager"
                    className="border-0"
                  />
                </div>
              )}
              <div className="relative self-center  mt-8  md:mt-16  xl:mt-40">
                <div className="hidden md:block text-5xl font-bold text-landing-orange text-center">
                  {formatPrice(annualIncome)}
                </div>
                <div className="hidden md:block mt-6 text-2xl font-bold leading-tight text-landing-blue text-center">
                  Annual household income
                </div>
              </div>
              <Description className="mt-10 mb-7 md:hidden" />

              <div className="md:hidden relative flex items-center justify-between mb-4 md:mt-10 xl:mt-0">
                <div className="md:hidden mr-3 text-lg font-bold leading-tight text-landing-blue">
                  Annual household income
                </div>
                <div className="md:hidden text-2xl font-bold text-landing-orange">
                  {formatPrice(annualIncome)}
                </div>
              </div>
              <div className="mt-6">
                <Slider
                  min={MIN_ANNUAL_INCOME}
                  max={MAX_ANNUAL_INCOME}
                  onChange={sliderOnChange}
                  value={annualIncome}
                  step={1000}
                  trackStyle={{
                    backgroundColor: "#F39237",
                    height: 16,
                    borderRadius: "30px",
                  }}
                  handleStyle={{
                    borderColor: "#272F42",
                    height: 28,
                    width: 28,
                    marginLeft: 0,
                    marginTop: -6,
                    backgroundColor: "#272F42",
                  }}
                  railStyle={{
                    backgroundColor: "#F0F3FD",
                    height: 16,
                    borderRadius: "30px",
                  }}
                />
              </div>
              <div className="w-full">
                {width >= Screen.DESKTOP_1280 ? (
                  <CalculatorDesktop keyzyBudget={keyzyBudget as BudgetModel} onHowIsPossible={onHowIsPossible} />
                ) : (
                  <CalculatorMobile keyzyBudget={keyzyBudget as BudgetModel} onHowIsPossible={onHowIsPossible} />
                )}
              </div>
            </div>
          </div>
          <div className="md:ml-7 xl:ml-20 md:max-w-[24.1rem] xl:max-w-[26rem] md:w-2/5 lg:1/2 xl:w-2/5">
            <div>
              <Description className="hidden xl:w-96 md:inline-block" />
              <Buttons className="-top-64 md:top-0 md:max-w-[15.5rem]" />
            </div>
          </div>
        </div>
      </SubLayout>
      <HorizontalBar className="inline-block my-4 md:hidden" />
    </div>
  );
};

export default BudgetCalculator;
